import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle,
  Clock,
  AlertTriangle,
  X,
  RotateCcw,
  FileText,
  Users,
  Calendar,
  HardDrive,
  Activity
} from 'lucide-react';

/**
 * BulkOperationsProgress - Real-time progress tracking for bulk operations
 * 
 * Features:
 * - Real-time progress updates
 * - Operation status tracking
 * - Individual session progress
 * - Error handling and retry options
 * - Operation cancellation
 * - Summary statistics
 */
const BulkOperationsProgress = ({ 
  operation = null,
  onCancel,
  onRetry,
  onComplete,
  className = ""
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [sessionProgress, setSessionProgress] = useState({});
  const [overallProgress, setOverallProgress] = useState(0);
  const [errors, setErrors] = useState([]);
  const [completedSessions, setCompletedSessions] = useState([]);
  const [statistics, setStatistics] = useState({
    totalFiles: 0,
    totalSize: 0,
    versionsCreated: 0,
    sosAffected: 0
  });

  // Mock operation steps
  const operationSteps = [
    { id: 'validate', name: 'Validating Files', description: 'Checking file integrity and permissions' },
    { id: 'upload', name: 'Uploading Files', description: 'Transferring files to CDN storage' },
    { id: 'versions', name: 'Creating Versions', description: 'Generating new material versions' },
    { id: 'distribute', name: 'Distributing Updates', description: 'Applying updates to selected sessions' },
    { id: 'finalize', name: 'Finalizing Operation', description: 'Completing operation and cleanup' }
  ];

  // Simulate progress updates when operation changes
  useEffect(() => {
    if (!operation) return;

    let cancelled = false;

    const simulateProgress = async () => {
      try {
        // Initialize progress tracking
        const sessionProgressMap = {};
        operation.selectedSessions.forEach(sessionId => {
          sessionProgressMap[sessionId] = { status: 'pending', progress: 0, error: null };
        });
        setSessionProgress(sessionProgressMap);

        // Process each step
        for (let i = 0; i < operationSteps.length; i++) {
          if (cancelled) break;
          
          setCurrentStep(i);
          const step = operationSteps[i];
          
          // Simulate step duration based on step type
          let stepDuration;
          switch (step.id) {
            case 'validate':
              stepDuration = 1000;
              break;
            case 'upload':
              stepDuration = operation.files?.length * 500 || 2000;
              break;
            case 'versions':
              stepDuration = operation.selectedSessions.length * 300;
              break;
            case 'distribute':
              stepDuration = operation.selectedSessions.length * 200;
              break;
            default:
              stepDuration = 1000;
          }

          // Simulate step progress
          const stepProgress = 20; // Each step contributes 20% to overall progress
          for (let progress = 0; progress <= 100; progress += 10) {
            if (cancelled) break;
            
            const overallPercent = (i * stepProgress) + (progress * stepProgress / 100);
            setOverallProgress(overallPercent);
            
            // Update session progress during distribute step
            if (step.id === 'distribute') {
              const updatedSessionProgress = { ...sessionProgressMap };
              operation.selectedSessions.forEach((sessionId, index) => {
                const sessionPercent = Math.min(100, progress + (index * 10));
                updatedSessionProgress[sessionId] = {
                  status: sessionPercent >= 100 ? 'completed' : 'processing',
                  progress: sessionPercent,
                  error: null
                };
                
                if (sessionPercent >= 100 && !completedSessions.includes(sessionId)) {
                  setCompletedSessions(prev => [...prev, sessionId]);
                  
                  // Update statistics
                  setStatistics(prev => ({
                    ...prev,
                    versionsCreated: prev.versionsCreated + 1,
                    sosAffected: prev.sosAffected + (operation.impact?.sessionsToSOsMap?.[sessionId] || 1)
                  }));
                }
              });
              setSessionProgress(updatedSessionProgress);
            }
            
            await new Promise(resolve => setTimeout(resolve, stepDuration / 10));
          }
        }

        if (!cancelled) {
          setOverallProgress(100);
          setCurrentStep(operationSteps.length);
          
          // Final statistics update
          setStatistics({
            totalFiles: operation.files?.length || 0,
            totalSize: operation.totalSize || 0,
            versionsCreated: operation.selectedSessions.length,
            sosAffected: operation.impact?.totalSOsAffected || 0
          });

          if (onComplete) {
            onComplete({
              success: true,
              statistics,
              completedSessions,
              operation
            });
          }
        }
      } catch (error) {
        console.error('Progress simulation error:', error);
        setErrors(prev => [...prev, {
          id: Date.now(),
          message: 'Progress tracking error',
          details: error.message
        }]);
      }
    };

    simulateProgress();

    return () => {
      cancelled = true;
    };
  }, [operation]);

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleRetry = () => {
    setCurrentStep(0);
    setOverallProgress(0);
    setSessionProgress({});
    setErrors([]);
    setCompletedSessions([]);
    setStatistics({
      totalFiles: 0,
      totalSize: 0,
      versionsCreated: 0,
      sosAffected: 0
    });
    
    if (onRetry) {
      onRetry();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStepIcon = (stepIndex) => {
    if (stepIndex < currentStep) {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    } else if (stepIndex === currentStep) {
      return <Clock className="h-5 w-5 text-blue-500 animate-spin" />;
    } else {
      return <div className="h-5 w-5 border-2 border-gray-300 rounded-full" />;
    }
  };

  const isCompleted = overallProgress >= 100;
  const hasErrors = errors.length > 0;
  const isProcessing = operation && !isCompleted && !hasErrors;

  if (!operation) {
    return (
      <Card className={className}>
        <CardContent className="p-8 text-center">
          <Activity className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500">No active bulk operations</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Activity className="h-5 w-5" />
            <span>Bulk Operation Progress</span>
          </div>
          <Badge 
            variant={isCompleted ? "default" : hasErrors ? "destructive" : "secondary"}
            className={
              isCompleted 
                ? "bg-green-100 text-green-800" 
                : hasErrors 
                ? "" 
                : "bg-blue-100 text-blue-800"
            }
          >
            {isCompleted ? 'Completed' : hasErrors ? 'Error' : 'In Progress'}
          </Badge>
        </CardTitle>
        <CardDescription>
          {operation.operationType || 'Materials Update'} • {operation.selectedSessions.length} sessions
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Overall Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Overall Progress</span>
            <span className="text-gray-600">{Math.round(overallProgress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all duration-500 ${
                isCompleted 
                  ? 'bg-green-500' 
                  : hasErrors 
                  ? 'bg-red-500' 
                  : 'bg-blue-500'
              }`}
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>

        {/* Operation Steps */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-gray-900">Operation Steps</h3>
          <div className="space-y-2">
            {operationSteps.map((step, index) => (
              <div
                key={step.id}
                className={`flex items-center space-x-3 p-2 rounded-lg ${
                  index < currentStep 
                    ? 'bg-green-50' 
                    : index === currentStep 
                    ? 'bg-blue-50' 
                    : 'bg-gray-50'
                }`}
              >
                {getStepIcon(index)}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${
                    index <= currentStep ? 'text-gray-900' : 'text-gray-500'
                  }`}>
                    {step.name}
                  </p>
                  <p className="text-xs text-gray-500">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Session Progress (during processing) */}
        {isProcessing && Object.keys(sessionProgress).length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-900">Session Progress</h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {Object.entries(sessionProgress).map(([sessionId, progress]) => (
                <div key={sessionId} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center space-x-2">
                    {progress.status === 'completed' ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : progress.status === 'processing' ? (
                      <Clock className="h-4 w-4 text-blue-500 animate-pulse" />
                    ) : progress.error ? (
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    ) : (
                      <div className="h-4 w-4 border-2 border-gray-300 rounded-full" />
                    )}
                    <span className="text-sm font-medium">Session {sessionId}</span>
                  </div>
                  <span className="text-xs text-gray-500">{progress.progress}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-center mb-1">
              <FileText className="h-4 w-4 text-blue-600" />
            </div>
            <p className="text-lg font-semibold text-blue-900">{statistics.totalFiles}</p>
            <p className="text-xs text-blue-600">Files Processed</p>
          </div>
          
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="flex items-center justify-center mb-1">
              <Calendar className="h-4 w-4 text-green-600" />
            </div>
            <p className="text-lg font-semibold text-green-900">{statistics.versionsCreated}</p>
            <p className="text-xs text-green-600">Versions Created</p>
          </div>
          
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <div className="flex items-center justify-center mb-1">
              <Users className="h-4 w-4 text-purple-600" />
            </div>
            <p className="text-lg font-semibold text-purple-900">{statistics.sosAffected}</p>
            <p className="text-xs text-purple-600">SOs Affected</p>
          </div>
          
          <div className="text-center p-3 bg-orange-50 rounded-lg">
            <div className="flex items-center justify-center mb-1">
              <HardDrive className="h-4 w-4 text-orange-600" />
            </div>
            <p className="text-lg font-semibold text-orange-900">{formatFileSize(statistics.totalSize)}</p>
            <p className="text-xs text-orange-600">Data Processed</p>
          </div>
        </div>

        {/* Error Messages */}
        {errors.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-red-900">Errors</h3>
            {errors.map((error) => (
              <div key={error.id} className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800">{error.message}</p>
                  {error.details && (
                    <p className="text-xs text-red-600 mt-1">{error.details}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-gray-600">
            {isCompleted && (
              <span className="text-green-600 font-medium">
                ✓ Operation completed successfully
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {hasErrors && (
              <Button
                onClick={handleRetry}
                variant="outline"
                size="sm"
                className="flex items-center space-x-1"
              >
                <RotateCcw className="h-4 w-4" />
                <span>Retry</span>
              </Button>
            )}
            
            {isProcessing && (
              <Button
                onClick={handleCancel}
                variant="outline"
                size="sm"
                className="flex items-center space-x-1"
              >
                <X className="h-4 w-4" />
                <span>Cancel</span>
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BulkOperationsProgress;